/**
 * /pricing — the subscription page (tiers T10, approved mock
 * `mockups/subscription-page/desktop.html`). Beta-gated: builds without
 * SUBSCRIPTIONS_ENABLED keep the original coming-soon stub (the prod
 * launch bundle ships guest + subscriptions + Vera A2A together).
 *
 * States: signed-out (FREE row CTAs to signup, Subscribe buttons route
 * through signup with ?next=/pricing), signed-in free (Subscribe starts
 * checkout), subscriber (CURRENT PLAN card + manage strip → Stripe
 * portal). Enterprise licensing contact and the FR-LEGAL rates line
 * render always.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LegalShell from '../components/legal/LegalShell';
import { useAuth } from '../auth/useAuth.ts';
import { withNext } from '../auth/postAuthRedirect.ts';
import {
  openPortal,
  startCheckout,
  tierOfGroups,
  type PlanLookupKey,
} from '../agents/billingApi.ts';
import { SUBSCRIPTIONS_ENABLED } from '../config/features.ts';
import './Pricing.css';

type Billing = 'monthly' | 'annual';
type Tier = 'premium' | 'plus' | 'pro';

interface Plan {
  tier: Tier;
  label: string;
  monthly: string;
  annualPerMonth: string;
  annualTotal: string;
  budget: string;
  features: readonly string[];
  hot?: boolean;
}

const PLANS: readonly Plan[] = [
  {
    tier: 'premium',
    label: 'PREMIUM',
    monthly: '$9.99',
    annualPerMonth: '$9.16',
    annualTotal: '$109.89',
    budget: '1M',
    features: [
      'All agents — Uno & Vera (Maximus at launch)',
      'Vera workflows + scheduled automation',
      'Documents & files, durable memory',
      'Email & text-message delivery',
      'No ads',
    ],
  },
  {
    tier: 'plus',
    label: 'PLUS',
    monthly: '$29.99',
    annualPerMonth: '$27.49',
    annualTotal: '$329.89',
    budget: '3M',
    features: [
      'Everything in Premium',
      '3× the daily budget — for daily drivers',
      'Longer research sessions & bigger workflows',
      'Priority access to new agents',
    ],
    hot: true,
  },
  {
    tier: 'pro',
    label: 'PRO',
    monthly: '$99.99',
    annualPerMonth: '$91.66',
    annualTotal: '$1,099.89',
    budget: '10M',
    features: [
      'Everything in Plus',
      "10× Premium's budget — professional workloads",
      'Heavy scheduled automation',
      'Early access to Maximus actions',
    ],
  },
];

/** The original stub, kept verbatim for flag-off (prod) builds. */
function ComingSoon() {
  return (
    <LegalShell title="Pricing" version="1.0" effective="2026-06-21">
      <p>
        Every OculusMind.AI agent currently runs on a <strong>free, limited tier</strong>{' '}
        while we train and refine it. You can use your agents and the A2A API today at no
        cost, subject to daily usage limits.
      </p>
      <h2>Coming soon</h2>
      <p>
        Premium individual plans and business plans are on the way — with higher limits and
        additional capabilities. We&rsquo;ll announce details here before they launch.
      </p>
    </LegalShell>
  );
}

export default function Pricing() {
  if (!SUBSCRIPTIONS_ENABLED) {
    return <ComingSoon />;
  }
  return <Plans />;
}

function Plans() {
  const { status, user } = useAuth();
  const navigate = useNavigate();
  const [billing, setBilling] = useState<Billing>('annual');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const signedIn = status === 'authenticated';
  const currentTier = signedIn ? tierOfGroups(user?.groups ?? []) : null;

  const subscribe = (plan: Plan) => {
    if (!signedIn) {
      navigate(withNext('/auth/signup', '/pricing'));
      return;
    }
    const lookupKey = `${plan.tier}_${billing}` as PlanLookupKey;
    setBusy(plan.tier);
    setError(null);
    void startCheckout(lookupKey)
      .then((url) => window.location.assign(url))
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'Checkout could not be started.');
        setBusy(null);
      });
  };

  const manage = () => {
    setBusy('manage');
    setError(null);
    void openPortal()
      .then((url) => {
        if (url === null) {
          setError('No billing account found yet — subscribe to a plan first.');
          setBusy(null);
        } else {
          window.location.assign(url);
        }
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'The billing portal could not be opened.');
        setBusy(null);
      });
  };

  return (
    <main className="om-sub">
      <div className="om-sub__eyebrow">PLANS · OM TOKEN BUDGETS</div>
      <h1 className="om-sub__title">Choose Your Plan</h1>
      <p className="om-sub__lede">
        Every paid plan unlocks all agents and capabilities — documents, durable memory, email
        &amp; text delivery, Vera&rsquo;s workflows with scheduled automation, and an{' '}
        <strong>ad-free experience</strong>. Plans differ by daily <strong>OM token budget</strong>:
        everything your agents do — model reasoning, tool calls, messages — draws from it.
      </p>

      <div className="om-sub__toggle" role="tablist" aria-label="Billing period">
        <button
          type="button"
          className={billing === 'monthly' ? 'is-on' : ''}
          onClick={() => setBilling('monthly')}
        >
          MONTHLY
        </button>
        <button
          type="button"
          className={billing === 'annual' ? 'is-on' : ''}
          onClick={() => setBilling('annual')}
        >
          ANNUAL <span className="save">SAVE ~8%</span>
        </button>
      </div>

      {error !== null ? (
        <p className="om-sub__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="om-sub__grid">
        {PLANS.map((plan) => {
          const isCurrent = currentTier === plan.tier;
          return (
            <section
              key={plan.tier}
              className={plan.hot === true ? 'om-sub__card om-sub__card--hot' : 'om-sub__card'}
            >
              {plan.hot === true ? <div className="om-sub__flag">MOST POPULAR</div> : null}
              <div className="om-sub__tier">{plan.label}</div>
              <div className="om-sub__price">
                <b>{billing === 'annual' ? plan.annualPerMonth : plan.monthly}</b>
                <span>
                  {billing === 'annual' ? `/mo · billed ${plan.annualTotal}/yr` : '/mo · billed monthly'}
                </span>
              </div>
              <div className="om-sub__annual-note">
                {billing === 'annual' ? 'two months on us vs monthly' : ' '}
              </div>
              <div className="om-sub__budget">
                <b>{plan.budget} OM</b> tokens / day
              </div>
              <ul className="om-sub__feats">
                {plan.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              {isCurrent ? (
                <span className="om-sub__cta om-sub__cta--current">CURRENT PLAN</span>
              ) : (
                <button
                  type="button"
                  className="om-sub__cta"
                  disabled={busy !== null}
                  onClick={() => subscribe(plan)}
                >
                  {busy === plan.tier ? 'STARTING CHECKOUT…' : `SUBSCRIBE — ${plan.label}`}
                </button>
              )}
            </section>
          );
        })}
      </div>

      {signedIn && currentTier !== null ? (
        <div className="om-sub__manage">
          <div>
            <b>YOUR PLAN: {currentTier.toUpperCase()}</b> &nbsp;Manage billing, switch plans, or
            cancel anytime.
          </div>
          <button
            type="button"
            className="om-sub__cta"
            disabled={busy !== null}
            onClick={manage}
          >
            {busy === 'manage' ? 'OPENING…' : 'MANAGE SUBSCRIPTION'}
          </button>
        </div>
      ) : (
        <div className="om-sub__free">
          <div>
            <b>FREE</b> &nbsp;Every account starts free (ad-supported): all core tools, a small
            daily budget, documents and memory. Guests can try Uno free with research tools only.
            Upgrade to any paid plan to remove ads.
          </div>
          {signedIn ? null : (
            <Link className="om-sub__cta" to={withNext('/auth/signup', '/pricing')}>
              CREATE FREE ACCOUNT
            </Link>
          )}
        </div>
      )}

      <div className="om-sub__enterprise">
        <div>
          <b>ENTERPRISE LICENSING</b> &nbsp;Custom OM budgets, seats for your whole team, private
          deployment options, and invoiced billing. Contact us for custom pricing.
        </div>
        <a className="om-sub__cta" href="mailto:help@oculusmind.ai">
          help@oculusmind.ai
        </a>
      </div>

      <p className="om-sub__legal">
        OM token budgets meter model usage, tool calls, and messaging (email/SMS at cost-based
        rates). Rates per operation may change without notice — see the{' '}
        <Link to="/legal/terms">Terms of Service</Link>. Subscriptions renew automatically;
        manage or cancel anytime via the billing portal. Prices in USD.
      </p>
    </main>
  );
}
